import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { LOCAL_TOPICS, topicLabel } from '@gogrov/shared'
import { supabase } from '@/lib/supabase'
import { functionError } from '@/hooks/useMix'

/**
 * Internal ops: the queues nobody else sees. Keywords waiting on research,
 * season records Emma was unsure of, the same keyword across accounts, the
 * season boundaries coming up, closing seasons with nowhere to go next, and
 * what each place publishes as public record.
 */

interface Awaiting { topic: string; term: string; term_norm: string; scope: string; clients: number; first_added: string; researched_at: string | null }
interface Collision { topic: string; term: string; term_norm: string; clients: number; accounts: string[] }
interface Place { place_key: string; name: string; state: string | null }
interface Overview { awaiting: Awaiting[]; collisions: Collision[]; places: Place[] }
interface Season {
  topic: string; term_norm: string; is_seasonal: boolean; window_open: string | null; window_close: string | null
  close_shape: 'fade' | 'cliff' | null; close_lead_days: number; successors: string[]; confidence: 'low' | 'medium' | 'high' | null
  researched_at: string
}
interface Capability { place_key: string; topic: string; availability: 'records' | 'partial' | 'news'; note: string | null; updated_at: string }

type Tab = 'research' | 'seasons' | 'calendar' | 'collisions' | 'places'
const TABS: { id: Tab; label: string }[] = [
  { id: 'research', label: 'Awaiting research' },
  { id: 'seasons', label: 'Season records' },
  { id: 'calendar', label: 'Boundary calendar' },
  { id: 'collisions', label: 'Like groups' },
  { id: 'places', label: 'Place capabilities' },
]

function useOverview() {
  return useQuery({
    queryKey: ['admin', 'ops'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_ops_overview')
      if (error) throw error
      return data as Overview
    },
  })
}

function useSeasons() {
  return useQuery({
    queryKey: ['admin', 'seasons'],
    queryFn: async () => {
      const { data, error } = await supabase.from('pill_seasonality').select('*').eq('is_seasonal', true).order('topic')
      if (error) throw error
      return data as Season[]
    },
  })
}

function useCapabilities() {
  return useQuery({
    queryKey: ['admin', 'capabilities'],
    queryFn: async () => {
      const { data, error } = await supabase.from('place_capabilities').select('*')
      if (error) throw error
      return data as Capability[]
    },
  })
}

/** The next date a MM-DD falls on, today or later. */
function nextDate(mmdd: string | null): Date | null {
  if (!mmdd) return null
  const [m, d] = mmdd.split('-').map(Number)
  const now = new Date(); now.setHours(0, 0, 0, 0)
  let at = new Date(now.getFullYear(), m - 1, Math.min(d, m === 2 ? 28 : d))
  if (at < now) at = new Date(now.getFullYear() + 1, m - 1, Math.min(d, m === 2 ? 28 : d))
  return at
}
const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

export function AdminOps() {
  const [tab, setTab] = useState<Tab>('research')
  const { data: overview, error } = useOverview()
  const { data: seasons } = useSeasons()
  const needs = (seasons ?? []).filter((s) => (s.successors?.length ?? 0) === 0 || !s.confidence || s.confidence === 'low').length
  const counts: Partial<Record<Tab, number>> = {
    research: overview?.awaiting.length, seasons: needs, collisions: overview?.collisions.length,
  }

  return (
    <div className="admin-client-view">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Ops</h1>
          <p className="admin-page-sub">Research queues, seasons and place records. Internal only.</p>
        </div>
      </div>
      <div className="admin-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`admin-tab${tab === t.id ? ' admin-tab--active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}{counts[t.id] ? ` (${counts[t.id]})` : ''}
          </button>
        ))}
      </div>
      {error && <p className="admin-alert admin-alert--error">{(error as Error).message}</p>}
      <div className="admin-section ops">
        {tab === 'research' && <Research rows={overview?.awaiting} />}
        {tab === 'seasons' && <Seasons rows={seasons} />}
        {tab === 'calendar' && <Calendar rows={seasons} />}
        {tab === 'collisions' && <Collisions rows={overview?.collisions} />}
        {tab === 'places' && <Places places={overview?.places} />}
      </div>
    </div>
  )
}

function Research({ rows }: { rows?: Awaiting[] }) {
  if (!rows) return <p className="admin-loading">Loading.</p>
  if (!rows.length) return <p className="admin-empty-hint">Every keyword has current research.</p>
  return (
    <>
      <p className="admin-page-sub">Keywords added but never researched, or researched over 60 days ago. Research is shared, so one run covers every account holding it.</p>
      <table className="admin-briefs-table">
        <thead><tr><th>Keyword</th><th>Topic</th><th>Scope</th><th>Accounts</th><th>Added</th><th>Last researched</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.topic}|${r.term_norm}|${r.scope}`}>
              <td><strong>{r.term}</strong></td><td>{topicLabel(r.topic)}</td><td>{r.scope}</td><td>{r.clients}</td>
              <td>{new Date(r.first_added).toLocaleDateString()}</td>
              <td>{r.researched_at ? new Date(r.researched_at).toLocaleDateString() : 'Never'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function Seasons({ rows }: { rows?: Season[] }) {
  const [filter, setFilter] = useState<'unsure' | 'gaps' | 'all'>('unsure')
  if (!rows) return <p className="admin-loading">Loading.</p>
  const shown = rows.filter((s) => filter === 'all' ? true
    : filter === 'gaps' ? (s.successors?.length ?? 0) === 0
    : !s.confidence || s.confidence === 'low')
  return (
    <>
      <div className="ops__filters">
        {([['unsure', 'Low confidence'], ['gaps', 'No successor'], ['all', 'All seasonal']] as const).map(([id, label]) => (
          <button key={id} className={filter === id ? 'btn-primary' : 'btn-secondary'} onClick={() => setFilter(id)}>{label}</button>
        ))}
      </div>
      <p className="admin-page-sub">
        A closing season with no successor gives Emma nothing to suggest. Saving a record here marks it high confidence.
      </p>
      {shown.length === 0 && <p className="admin-empty-hint">Nothing here.</p>}
      <div className="ops__cards">
        {shown.map((s) => <SeasonEditor key={`${s.topic}|${s.term_norm}`} row={s} />)}
      </div>
    </>
  )
}

function SeasonEditor({ row }: { row: Season }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(row.window_open ?? '')
  const [close, setClose] = useState(row.window_close ?? '')
  const [shape, setShape] = useState(row.close_shape ?? 'fade')
  const [next, setNext] = useState((row.successors ?? []).join('; '))
  const [msg, setMsg] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async (seasonal: boolean) => {
      const valid = /^\d{2}-\d{2}$/
      if (seasonal && (!valid.test(open) || !valid.test(close))) throw new Error('Windows are MM-DD, like 06-01.')
      const { error } = await supabase.from('pill_seasonality').update({
        is_seasonal: seasonal,
        window_open: seasonal ? open : null, window_close: seasonal ? close : null,
        close_shape: seasonal ? shape : null,
        successors: seasonal ? next.split(';').map((x) => x.trim()).filter(Boolean).slice(0, 3) : [],
        confidence: 'high', researched_at: new Date().toISOString(),
      }).eq('topic', row.topic).eq('term_norm', row.term_norm)
      if (error) throw error
    },
    onSuccess: () => { setMsg('Saved'); void qc.invalidateQueries({ queryKey: ['admin', 'seasons'] }) },
    onError: (e) => setMsg((e as Error).message),
  })
  return (
    <div className="ops__card">
      <div className="ops__cardhead">
        <strong>{row.term_norm}</strong>
        <span>{topicLabel(row.topic)} · {row.confidence ?? 'no'} confidence</span>
      </div>
      <div className="ops__fields">
        <label>Opens<input className="admin-input" value={open} onChange={(e) => setOpen(e.target.value)} placeholder="MM-DD" maxLength={5} /></label>
        <label>Closes<input className="admin-input" value={close} onChange={(e) => setClose(e.target.value)} placeholder="MM-DD" maxLength={5} /></label>
        <label>Ends as
          <select className="admin-select" value={shape} onChange={(e) => setShape(e.target.value as 'fade' | 'cliff')}>
            <option value="fade">Fade</option><option value="cliff">Cliff</option>
          </select>
        </label>
        <label className="ops__wide">Comes next (separate with ;)<input className="admin-input" value={next} onChange={(e) => setNext(e.target.value)} placeholder="e.g. tornado season; flooding" /></label>
      </div>
      <div className="ops__actions">
        {msg && <span className="ops__msg">{msg}</span>}
        <button className="btn-secondary" disabled={save.isPending} onClick={() => { setMsg(null); save.mutate(false) }}>Not seasonal</button>
        <button className="btn-primary" disabled={save.isPending} onClick={() => { setMsg(null); save.mutate(true) }}>Save</button>
      </div>
    </div>
  )
}

function Calendar({ rows }: { rows?: Season[] }) {
  const events = useMemo(() => {
    const horizon = Date.now() + 90 * 86_400_000
    return (rows ?? []).flatMap((s) => {
      const o = nextDate(s.window_open)
      const c = nextDate(s.window_close)
      const lead = c ? new Date(c.getTime() - s.close_lead_days * 86_400_000) : null
      return [
        o && { at: o, kind: 'Opens', s },
        lead && lead.getTime() >= Date.now() - 86_400_000 && { at: lead, kind: 'Closing notice', s },
        c && { at: c, kind: 'Closes', s },
      ].filter((x): x is { at: Date; kind: string; s: Season } => !!x && x.at.getTime() <= horizon)
    }).sort((a, b) => a.at.getTime() - b.at.getTime())
  }, [rows])
  if (!rows) return <p className="admin-loading">Loading.</p>
  if (!events.length) return <p className="admin-empty-hint">No season boundaries in the next 90 days.</p>
  return (
    <>
      <p className="admin-page-sub">Season boundaries in the next 90 days. Closing notices go out ahead of the close by each record's lead time.</p>
      <table className="admin-briefs-table">
        <thead><tr><th>Date</th><th>Event</th><th>Keyword</th><th>Topic</th><th>Next</th></tr></thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={i}>
              <td className="mono">{fmt(e.at)}</td><td>{e.kind}</td><td><strong>{e.s.term_norm}</strong></td>
              <td>{topicLabel(e.s.topic)}</td>
              <td>{e.s.successors?.length ? e.s.successors.join(', ') : <span className="ops__warn">None set</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function Collisions({ rows }: { rows?: Collision[] }) {
  if (!rows) return <p className="admin-loading">Loading.</p>
  if (!rows.length) return <p className="admin-empty-hint">No keyword is held by more than one account.</p>
  return (
    <>
      <p className="admin-page-sub">The same keyword under the same topic in more than one account. They share research, and in a shared market they will pull the same stories.</p>
      <table className="admin-briefs-table">
        <thead><tr><th>Keyword</th><th>Topic</th><th>Accounts</th><th>Who</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.topic}|${r.term_norm}`}>
              <td><strong>{r.term}</strong></td><td>{topicLabel(r.topic)}</td><td>{r.clients}</td><td>{r.accounts.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function Places({ places }: { places?: Place[] }) {
  const { data: caps } = useCapabilities()
  const [openKey, setOpenKey] = useState<string | null>(null)
  const qc = useQueryClient()
  const [msg, setMsg] = useState<Record<string, string>>({})
  const survey = useMutation({
    mutationFn: async (p: Place) => {
      const { data, error } = await supabase.functions.invoke('place-survey', { body: { name: p.name, state: p.state } })
      if (error) throw await functionError(error)
      return data as { surveyed: number; records: number; partial: number }
    },
    onSuccess: (d, p) => {
      setMsg((m) => ({ ...m, [p.place_key]: `Surveyed ${d.surveyed} topics: ${d.records} with records, ${d.partial} partial.` }))
      void qc.invalidateQueries({ queryKey: ['admin', 'capabilities'] })
    },
    onError: (e, p) => setMsg((m) => ({ ...m, [p.place_key]: (e as Error).message })),
  })
  const setAvail = useMutation({
    mutationFn: async (row: { place_key: string; topic: string; availability: string }) => {
      const { error } = await supabase.from('place_capabilities').upsert({ ...row, updated_at: new Date().toISOString() })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'capabilities'] }),
  })

  if (!places) return <p className="admin-loading">Loading.</p>
  if (!places.length) return <p className="admin-empty-hint">No active city or county places yet.</p>
  return (
    <>
      <p className="admin-page-sub">What each place publishes as public record. Accounts see this as "What your places can supply". Unsurveyed topics read as news only.</p>
      <div className="ops__cards">
        {places.map((p) => {
          const mine = (caps ?? []).filter((c) => c.place_key === p.place_key)
          const rec = mine.filter((c) => c.availability === 'records').length
          const part = mine.filter((c) => c.availability === 'partial').length
          const last = mine.reduce<string | null>((a, c) => (!a || c.updated_at > a ? c.updated_at : a), null)
          const running = survey.isPending && survey.variables?.place_key === p.place_key
          return (
            <div key={p.place_key} className="ops__card">
              <div className="ops__cardhead">
                <strong>{p.state ? `${p.name}, ${p.state}` : p.name}</strong>
                <span>{mine.length ? `${rec} records · ${part} partial · surveyed ${new Date(last!).toLocaleDateString()}` : 'Not surveyed'}</span>
              </div>
              {msg[p.place_key] && <p className="ops__msg">{msg[p.place_key]}</p>}
              <div className="ops__actions">
                <button className="btn-secondary" onClick={() => setOpenKey(openKey === p.place_key ? null : p.place_key)}>
                  {openKey === p.place_key ? 'Hide' : 'Edit by hand'}
                </button>
                <button className="btn-primary" disabled={survey.isPending} onClick={() => survey.mutate(p)}>
                  {running ? 'Surveying' : 'Survey with Emma'}
                </button>
              </div>
              {openKey === p.place_key && (
                <table className="admin-briefs-table">
                  <tbody>
                    {LOCAL_TOPICS.map((t) => {
                      const c = mine.find((x) => x.topic === t.id)
                      return (
                        <tr key={t.id}>
                          <td>{t.label}</td>
                          <td>
                            <select className="admin-select admin-select--sm" value={c?.availability ?? 'news'}
                              onChange={(e) => setAvail.mutate({ place_key: p.place_key, topic: t.id, availability: e.target.value })}>
                              <option value="records">Records</option><option value="partial">Some records</option><option value="news">News only</option>
                            </select>
                          </td>
                          <td className="ops__note">{c?.note ?? ''}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
